import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { Timer, Check } from 'lucide-react';
import AITutor from './AITutor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUESTIONS = [
    {
        q: 'A body of mass 2 kg experiences a net force of 10 N. Its acceleration is —',
        opts: ['2 m/s²', '5 m/s²', '10 m/s²', '20 m/s²'],
        a: 1,
    },
    {
        q: 'Which of the following is an isotope of Hydrogen?',
        opts: ['Deuterium', 'Helium-3', 'Lithium-7', 'Carbon-12'],
        a: 0,
    },
    {
        q: 'The derivative of sin(x)·cos(x) with respect to x is —',
        opts: ['cos(2x)', 'sin(2x)', '2sin(x)', '−cos(2x)'],
        a: 0,
    },
    {
        q: 'A word that means the opposite of "ephemeral":',
        opts: ['Fleeting', 'Momentary', 'Enduring', 'Rapid'],
        a: 2,
    },
    {
        q: 'If 3ˣ = 81, then x =',
        opts: ['2', '3', '4', '5'],
        a: 2,
    },
];

const DURATION = 90; // seconds

export default function Quizzes() {
    const [phase, setPhase] = useState('intro'); // intro | live | done
    const [idx, setIdx] = useState(0);
    const [answers, setAnswers] = useState([]);
    const [remaining, setRemaining] = useState(DURATION);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);
    const startedAt = useRef(null);

    useEffect(() => {
        if (phase !== 'live') return;
        if (remaining <= 0) {
            finish(answers);
            return;
        }
        const t = setTimeout(() => setRemaining((r) => r - 1), 1000);
        return () => clearTimeout(t);
    }, [phase, remaining]);

    function begin() {
        setPhase('live');
        setIdx(0);
        setAnswers([]);
        setRemaining(DURATION);
        setSaved(false);
        startedAt.current = Date.now();
    }

    function pick(i) {
        const next = [...answers, i];
        setAnswers(next);
        if (idx + 1 >= QUESTIONS.length) {
            finish(next);
        } else {
            setIdx(idx + 1);
        }
    }

    async function finish(final) {
        setPhase('done');
        const score = final.reduce((acc, a, i) => acc + (a === QUESTIONS[i].a ? 1 : 0), 0);
        const duration = Math.round((Date.now() - startedAt.current) / 1000);
        setSaving(true);
        try {
            await axios.post(`${API}/quiz/attempts`, {
                quiz_id: 'sample-mixed-01',
                score,
                total: QUESTIONS.length,
                duration_seconds: duration,
            });
            setSaved(true);
        } catch (e) {
            console.error(e);
        } finally {
            setSaving(false);
        }
    }

    const score = answers.reduce((acc, a, i) => acc + (a === QUESTIONS[i].a ? 1 : 0), 0);
    const pct = Math.round((score / QUESTIONS.length) * 100);

    return (
        <div data-testid="section-quizzes" className="space-y-10">
            <div className="flex items-start justify-between">
                <div className="mono text-[10px] text-[color:var(--moss-deep)]">§III — Timed Practice</div>
                <div className="mono text-[10px] text-[color:var(--moss-deep)]">
                    {QUESTIONS.length} Q · {DURATION}s
                </div>
            </div>

            <h2 className="display text-[color:var(--ink)]" style={{ fontSize: 'clamp(40px, 5vw, 68px)' }}>
                Practice under
                <br />
                <span className="display-italic" style={{ color: 'var(--lavender-deep)' }}>pressure.</span>
            </h2>

            {/* Metis — Gamified AI Study Advisor */}
            <AITutor />

            <AnimatePresence mode="wait">
                {phase === 'intro' && (
                    <motion.div
                        key="intro"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -12 }}
                        className="space-y-6"
                    >
                        <p className="text-[color:var(--ink-soft)] max-w-lg text-[15px] leading-relaxed">
                            A mixed sample from the shelf — physics, chemistry, maths &
                            verbal. Ninety seconds. No going back. See how you fare when the
                            clock is unforgiving.
                        </p>
                        <div className="grid grid-cols-3 gap-4 max-w-md">
                            <Stat n="90s" l="on the clock" />
                            <Stat n="5" l="questions" />
                            <Stat n="4" l="opts / q" />
                        </div>
                        <button
                            data-testid="quiz-start"
                            onClick={begin}
                            className="btn-lavender px-6 py-3 rounded-sm text-sm mono"
                        >
                            Start the timer →
                        </button>
                    </motion.div>
                )}

                {phase === 'live' && (
                    <motion.div
                        key="live"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="space-y-6"
                    >
                        <div className="flex items-center justify-between border-y border-[color:var(--moss-deep)] py-3">
                            <div className="mono text-[11px] text-[color:var(--moss-deep)]">
                                Q {idx + 1} of {QUESTIONS.length}
                            </div>
                            <div className="flex items-center gap-2 mono text-lg" data-testid="quiz-timer">
                                <Timer size={14} />
                                <span
                                    className="tabular-nums"
                                    style={{ color: remaining <= 15 ? 'var(--destructive-foreground)' : 'var(--moss-deep)' }}
                                >
                                    {String(Math.floor(remaining / 60)).padStart(2, '0')}:
                                    {String(remaining % 60).padStart(2, '0')}
                                </span>
                            </div>
                        </div>

                        <motion.div
                            key={idx}
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="space-y-4"
                        >
                            <div
                                className="display text-[color:var(--ink)]"
                                style={{ fontSize: 'clamp(22px, 2.6vw, 32px)' }}
                            >
                                {QUESTIONS[idx].q}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                {QUESTIONS[idx].opts.map((o, i) => (
                                    <button
                                        key={i}
                                        data-testid={`quiz-opt-${i}`}
                                        onClick={() => pick(i)}
                                        className="text-left border border-[color:var(--moss-deep)]/40 hover:border-[color:var(--lavender-deep)] bg-white/40 hover:bg-[color:var(--lavender)]/30 px-4 py-3 rounded-sm transition-all group"
                                    >
                                        <span className="mono text-[10px] text-[color:var(--moss-deep)] mr-3">
                                            {String.fromCharCode(65 + i)}
                                        </span>
                                        <span className="text-[color:var(--ink)] text-[15px] group-hover:italic transition-all">
                                            {o}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </motion.div>

                        <div className="w-full h-[3px] bg-[color:var(--moss-deep)]/20 rounded-full overflow-hidden">
                            <motion.div
                                className="h-full bg-[color:var(--lavender-deep)]"
                                animate={{ width: `${((idx + 1) / QUESTIONS.length) * 100}%` }}
                                transition={{ duration: 0.4 }}
                            />
                        </div>
                    </motion.div>
                )}

                {phase === 'done' && (
                    <motion.div
                        key="done"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="space-y-8"
                    >
                        <div className="border-y border-[color:var(--moss-deep)] py-8 text-center">
                            <div className="mono text-[10px] text-[color:var(--moss-deep)] mb-2">YOUR RESULT</div>
                            <div
                                className="display-italic"
                                style={{
                                    fontSize: 'clamp(80px, 12vw, 160px)',
                                    color: 'var(--lavender-deep)',
                                    lineHeight: 0.9,
                                }}
                            >
                                {score}<span className="text-[color:var(--ink)]">/{QUESTIONS.length}</span>
                            </div>
                            <div className="mono text-[11px] text-[color:var(--moss-deep)] mt-2">
                                {pct}% correct
                            </div>
                        </div>

                        <div className="space-y-2">
                            {QUESTIONS.map((q, i) => {
                                const ok = answers[i] === q.a;
                                return (
                                    <div
                                        key={i}
                                        className="flex items-center gap-3 text-[13px] border-b border-[color:var(--moss-deep)]/20 py-2"
                                    >
                                        <span
                                            className={`h-4 w-4 rounded-full flex items-center justify-center text-[9px] ${ok ? 'bg-[color:var(--lavender-deep)] text-white' : 'bg-[color:var(--moss-deep)]/20 text-[color:var(--moss-deep)]'}`}
                                        >
                                            {ok ? <Check size={10} /> : '×'}
                                        </span>
                                        <span className="text-[color:var(--ink)] flex-1 truncate">{q.q}</span>
                                        <span className="mono text-[10px] text-[color:var(--moss-deep)]">
                                            {q.opts[q.a]}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>

                        <div className="flex items-center gap-3">
                            <button
                                data-testid="quiz-retake"
                                onClick={begin}
                                className="btn-lavender px-5 py-2 rounded-sm text-xs mono"
                            >
                                Retake
                            </button>
                            <div className="mono text-[10px] text-[color:var(--moss-deep)]">
                                {saving ? 'Saving attempt…' : saved ? 'Attempt saved to your folio ✦' : ''}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

function Stat({ n, l }) {
    return (
        <div className="border-t border-[color:var(--moss-deep)] pt-3">
            <div className="display text-[color:var(--ink)] text-3xl">{n}</div>
            <div className="mono text-[10px] text-[color:var(--moss-deep)] mt-1">{l}</div>
        </div>
    );
}
